import { useCallback, useMemo, useState } from "react"
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { CircleCheck, Download, Flag, LifeBuoy, UserRoundCheck } from "lucide-react"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionLayout,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { ConversationPanel } from "@/components/conversation/ConversationPanel"
import { ConfirmDialog } from "@/components/tickets/ConfirmDialog"
import { BulkPickDialog } from "@/components/tickets/BulkPickDialog"
import { useRightRail } from "@/lib/right-rail"
import { downloadMappedCsv } from "@/lib/csv"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { getSessionUser } from "@/core/auth"
import {
  assignSupportTicket,
  closeSupportTicket,
  guestNameMap,
  guestsLookupQueryOptions,
  isTicketOpen,
  isTicketUnread,
  loadInboxBundle,
  memberNameMap,
  patchTicketInCache,
  priorityRank,
  setSupportTicketStatus,
  statusLabel,
  TICKET_STATUSES,
  ticketsKey,
  ticketsQueryOptions,
  type MemberRow,
  type SupportTicket,
  type TicketStatus,
} from "@/core/tickets"

// The Tickets LAYOUT route. Route DEPTH = Main columns (mirrors forms.tsx):
//   /tickets             → col 1 (inbox list)
//   /tickets/$ticketId   → col 1 + 2 (+ ticket thread)
// `$ticketId` lives on a MARKER child route; ColumnLayout decides how many fit.
// `?focus=2` fullscreens col 2. Legacy `?ticketId=…` redirects into the path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/tickets")({
  validateSearch: (s: Record<string, unknown>): { focus?: number; ticketId?: string } => {
    return {
      focus: parseRouteFocus(s.focus),
      ticketId: typeof s.ticketId === "string" ? s.ticketId : undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    if (search.ticketId) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/tickets/$ticketId",
        params: { ...params, ticketId: search.ticketId },
        search: { focus: search.focus },
      })
    }
  },
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(ticketsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: TicketsView,
})

const PRIORITY_BADGE: Record<string, string> = {
  urgent: "text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-500/15",
  high: "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15",
  normal: "text-plum-700/70 dark:text-plum-200/55 bg-plum-100 dark:bg-white/10",
  low: "text-plum-600/60 dark:text-plum-300/45 bg-plum-50 dark:bg-white/5",
}

function TicketsView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/tickets" })
  const { focus } = Route.useSearch()
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()
  const showInRail = useRightRail((s) => s.show)
  const currentUserId = useMemo(() => getSessionUser()?.id ?? null, [])

  const { ticketId: selectedId } = useRouteRecordSelection("ticketId")

  const openTicket = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/tickets/$ticketId",
        params: { ...params, ticketId: id },
        search: {},
      }),
    [navigate, params],
  )
  const openTicketFull = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/tickets/$ticketId",
        params: { ...params, ticketId: id },
        search: { focus: 2 },
      }),
    [navigate, params],
  )
  const closePane = useCallback(
    () => void navigate({ to: "/$orgSlug/$eventSlug/tickets", params, search: {} }),
    [navigate, params],
  )

  const bundleQ = useQuery({
    queryKey: ["inbox-bundle", eventId],
    queryFn: () => loadInboxBundle(qc, eventId!),
    enabled: !!eventId,
  })
  const ticketsQ = useQuery({ ...ticketsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const guestsQ = useQuery({ ...guestsLookupQueryOptions(eventId ?? ""), enabled: !!eventId })

  const names = useMemo(() => guestNameMap(guestsQ.data ?? []), [guestsQ.data])
  // linked userId → guest row id, so a ticket (keyed on guestUserId) can open
  // the full guest record in the rail.
  const guestIdByUser = useMemo(() => {
    const m = new Map<string, string>()
    for (const g of guestsQ.data ?? []) if (g.linkedUserId) m.set(g.linkedUserId, g.id)
    return m
  }, [guestsQ.data])
  const assignees = useMemo(() => memberNameMap(bundleQ.data?.members ?? []), [bundleQ.data])
  const memberList = useMemo(() => memberOptions(bundleQ.data?.members ?? []), [bundleQ.data])
  const tickets = ticketsQ.data ?? []

  // ── Bulk triage ──────────────────────────────────────────────────────────
  // The CollectionView bulk pill calls run(rows); each action stages the
  // selected rows + opens a small picker/confirm so the operator chooses a
  // target value (assignee / status) or confirms the destructive close.
  const [bulk, setBulk] = useState<
    | { kind: "assign"; rows: SupportTicket[] }
    | { kind: "status"; rows: SupportTicket[] }
    | { kind: "close"; rows: SupportTicket[] }
    | null
  >(null)

  const patchRow = useCallback(
    (t: { id: string } & Partial<SupportTicket>) => {
      const ok = patchTicketInCache(qc, eventId!, t)
      if (!ok) void qc.invalidateQueries({ queryKey: ticketsKey(eventId!) })
    },
    [qc, eventId],
  )

  const bulkAssign = useMutation({
    mutationFn: async ({ rows, userId }: { rows: SupportTicket[]; userId: string | null }) => {
      const results = await Promise.allSettled(
        rows.map((r) => assignSupportTicket(eventId!, r.id, userId)),
      )
      return { rows, userId, results }
    },
    onSuccess: ({ rows, userId, results }) => {
      results.forEach((res, i) => {
        if (res.status === "fulfilled") patchRow({ id: rows[i].id, assigneeUserId: userId })
      })
      const failed = results.filter((r) => r.status === "rejected").length
      setBulk(null)
      const who = userId ? (assignees.get(userId) ?? "the assignee") : "Unassigned"
      toast[failed ? "warning" : "success"](
        failed
          ? `Assigned ${results.length - failed}/${results.length} — ${failed} failed`
          : userId
            ? `Assigned ${results.length} to ${who}`
            : `Unassigned ${results.length}`,
      )
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Bulk assign failed"),
  })

  const bulkStatus = useMutation({
    mutationFn: async ({ rows, status }: { rows: SupportTicket[]; status: TicketStatus }) => {
      const results = await Promise.allSettled(
        rows.map((r) => setSupportTicketStatus(eventId!, r.id, status)),
      )
      return { rows, status, results }
    },
    onSuccess: ({ rows, status, results }) => {
      results.forEach((res, i) => {
        if (res.status === "fulfilled" && res.value.ticket) patchRow(res.value.ticket)
        else if (res.status === "fulfilled") patchRow({ id: rows[i].id, status })
      })
      const failed = results.filter((r) => r.status === "rejected").length
      setBulk(null)
      toast[failed ? "warning" : "success"](
        failed
          ? `Updated ${results.length - failed}/${results.length} — ${failed} failed`
          : `Marked ${results.length} as ${statusLabel(status)}`,
      )
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Bulk status failed"),
  })

  const bulkClose = useMutation({
    mutationFn: async (rows: SupportTicket[]) => {
      const results = await Promise.allSettled(rows.map((r) => closeSupportTicket(eventId!, r.id)))
      return { rows, results }
    },
    onSuccess: ({ rows, results }) => {
      results.forEach((res, i) => {
        if (res.status === "fulfilled" && res.value.ticket) patchRow(res.value.ticket)
        else if (res.status === "fulfilled")
          patchRow({ id: rows[i].id, status: "closed", closedAt: new Date().toISOString() })
      })
      const failed = results.filter((r) => r.status === "rejected").length
      setBulk(null)
      toast[failed ? "warning" : "success"](
        failed
          ? `Closed ${results.length - failed}/${results.length} — ${failed} failed`
          : `Closed ${results.length} ${results.length === 1 ? "ticket" : "tickets"}`,
      )
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Bulk close failed"),
  })


  const config = useMemo<CollectionConfig<SupportTicket>>(() => {
    const guest = (t: SupportTicket) => names.get(t.guestUserId) ?? "Guest"
    const assignee = (t: SupportTicket) =>
      t.assigneeUserId ? (assignees.get(t.assigneeUserId) ?? "Assigned") : null
    const exportCsv = (rows: SupportTicket[]) => {
      downloadMappedCsv(
        "cases.csv",
        ["Subject", "Event Person", "Priority", "Status", "Assignee", "Updated"],
        rows,
        (t) => [
          t.subject,
          guest(t),
          t.priority ?? "normal",
          isTicketOpen(t) ? t.status : "closed",
          assignee(t) ?? "",
          t.modifiedAt ?? t.createdAt,
        ],
      )
    }

    return {
      title: "Cases",
      getId: (t) => t.id,
      searchText: (t) => `${t.subject} ${guest(t)}`,
      onOpen: (t) => openTicket(t.id),
      getRecordHref: (t) => `/${params.orgSlug}/${params.eventSlug}/tickets/${t.id}`,
      onOpenFullscreen: (t) => openTicketFull(t.id),
      defaultSortId: "updated",
      defaultSortDir: "desc",
      emptyTitle: "No cases",
      emptyBlurb: "Messages from Event People to the team land here as cases.",
      emptyIcon: <LifeBuoy className="h-5 w-5" />,
      savedViews: [
        { id: "open", label: "Open", filter: isTicketOpen },
        { id: "all", label: "All", filter: () => true },
        { id: "closed", label: "Closed", filter: (t) => !isTicketOpen(t) },
      ],
      filters: [
        {
          id: "assignee",
          label: "Assignee",
          options: [
            ["all", "All"],
            ["me", "Assigned to me"],
            ["unassigned", "Unassigned"],
          ],
          predicate: (v, t) =>
            v === "me"
              ? !!currentUserId && t.assigneeUserId === currentUserId
              : v === "unassigned"
                ? t.assigneeUserId == null
                : true,
        },
        {
          id: "priority",
          label: "Priority",
          options: [
            ["all", "All"],
            ["urgent", "Urgent"],
            ["high", "High"],
            ["normal", "Normal"],
            ["low", "Low"],
          ],
          predicate: (v, t) => (t.priority ?? "normal") === v,
        },
      ],
      groupOptions: [
        {
          id: "status",
          label: "Status",
          groupOf: (t) => (isTicketOpen(t) ? statusLabel(t.status) : "Closed"),
          order: (l) => (l === "Closed" ? 99 : 0),
        },
        {
          id: "priority",
          label: "Priority",
          groupOf: (t) => cap(t.priority ?? "normal"),
          order: (l) => priorityRank(l.toLowerCase()),
        },
        {
          id: "assignee",
          label: "Assignee",
          groupOf: (t) => assignee(t) ?? "Unassigned",
          order: (l) => (l === "Unassigned" ? 99 : 0),
        },
      ],
      bulkActions: [
        { id: "assign", label: "Assign", icon: <UserRoundCheck className="h-4 w-4" />, run: (rows) => setBulk({ kind: "assign", rows }) },
        { id: "status", label: "Change status", icon: <Flag className="h-4 w-4" />, run: (rows) => setBulk({ kind: "status", rows }) },
        { id: "close", label: "Close", icon: <CircleCheck className="h-4 w-4" />, run: (rows) => setBulk({ kind: "close", rows }) },
        { id: "export", label: "Export", icon: <Download className="h-4 w-4" />, run: exportCsv },
      ],
      menuActions: [
        { id: "export-all", label: "Export all (CSV)", icon: <Download className="mb-mii" />, run: (rows) => exportCsv(rows) },
      ],
      columns: [
        {
          id: "subject",
          header: "Subject",
          sortable: true,
          sortValue: (t) => t.subject.toLowerCase(),
          // Unread is a leading accent dot baked into the subject (no separate
          // column) — a thin, scannable marker that keeps the row tight.
          render: (t) => (
            <span className="flex min-w-0 items-center gap-2">
              {isTicketUnread(t) && (
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-[var(--accent)]"
                  aria-label="Unread"
                  title="Unread"
                />
              )}
              <span className="truncate font-semibold text-plum-900 dark:text-plum-50">
                {t.subject || "Untitled ticket"}
              </span>
            </span>
          ),
        },
        {
          id: "guest",
          header: "Event Person",
          className: "text-plum-700/75 dark:text-plum-200/60",
          render: (t) => {
            const guestId = guestIdByUser.get(t.guestUserId)
            if (!guestId)
              return <span className="text-plum-700/75 dark:text-plum-200/60">{guest(t)}</span>
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  showInRail({ kind: "guest", guestId, name: guest(t) })
                }}
                className="rounded text-left hover:text-plum-900 hover:underline dark:hover:text-plum-50"
              >
                {guest(t)}
              </button>
            )
          },
        },
        {
          id: "priority",
          header: "Priority",
          sortable: true,
          sortValue: (t) => priorityRank(t.priority),
          render: (t) => {
            const p = t.priority ?? "normal"
            return (
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide uppercase ${PRIORITY_BADGE[p] ?? PRIORITY_BADGE.normal}`}>
                {p}
              </span>
            )
          },
        },
        {
          id: "status",
          header: "Status",
          render: (t) => {
            const open = isTicketOpen(t)
            return (
              <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${open ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400" : "bg-plum-100 text-plum-700/60 dark:bg-white/10 dark:text-plum-200/45"}`}>
                {open ? statusLabel(t.status) : "Closed"}
              </span>
            )
          },
        },
        {
          id: "assignee",
          header: "Assignee",
          className: "text-plum-700/70 dark:text-plum-200/55",
          render: (t) => {
            const name = assignee(t)
            if (!name || !t.assigneeUserId)
              return <span className="text-plum-700/40 dark:text-plum-200/30">Unassigned</span>
            // Assignees are staff — no guest record to open — so plain text.
            return <span>{name}</span>
          },
        },
        {
          id: "updated",
          header: "Updated",
          className: "text-plum-700/55 dark:text-plum-200/40",
          sortable: true,
          sortValue: (t) => t.modifiedAt ?? t.createdAt,
          render: (t) => relTime(t.modifiedAt ?? t.createdAt),
        },
      ],
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names, guestIdByUser, assignees, openTicket, openTicketFull, params, showInRail, currentUserId])

  const table = (
    <CollectionView
      config={config}
      rows={tickets}
      activeId={selectedId}
      loading={bundleQ.isLoading && tickets.length === 0}
      error={ticketsQ.isError ? ((ticketsQ.error as Error)?.message ?? "Failed to load tickets") : null}
    />
  )

  const columns = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [{ id: "list", minWidth: 320, width: 400, node: table }]
    if (selectedId) {
      cols.push({
        id: "thread",
        minWidth: 460,
        fill: true,
        node: <ConversationPanel key={selectedId} ticketId={selectedId} onBack={closePane} closeVariant="close" />,
      })
    }
    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, selectedId, closePane])

  return (
    <RoutedCollectionLayout columns={columns} focus={focus}>
      {/* Bulk: assign */}
      <BulkPickDialog
        open={bulk?.kind === "assign"}
        onOpenChange={(o) => !o && setBulk(null)}
        title={`Assign ${bulk?.rows.length ?? 0} ${(bulk?.rows.length ?? 0) === 1 ? "ticket" : "tickets"}`}
        options={[
          { value: "", label: "Unassigned" },
          ...memberList.map((m) => ({ value: m.userId, label: m.name })),
        ]}
        busy={bulkAssign.isPending}
        onPick={(value) =>
          bulk?.kind === "assign" &&
          bulkAssign.mutate({ rows: bulk.rows, userId: value || null })
        }
      />

      {/* Bulk: change status */}
      <BulkPickDialog
        open={bulk?.kind === "status"}
        onOpenChange={(o) => !o && setBulk(null)}
        title={`Change status · ${bulk?.rows.length ?? 0} ${(bulk?.rows.length ?? 0) === 1 ? "ticket" : "tickets"}`}
        options={TICKET_STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))}
        busy={bulkStatus.isPending}
        onPick={(value) =>
          bulk?.kind === "status" &&
          bulkStatus.mutate({ rows: bulk.rows, status: value as TicketStatus })
        }
      />

      {/* Bulk: close (destructive → confirm with blast radius) */}
      <ConfirmDialog
        open={bulk?.kind === "close"}
        onOpenChange={(o) => !o && setBulk(null)}
        title={`Close ${bulk?.rows.length ?? 0} ${(bulk?.rows.length ?? 0) === 1 ? "ticket" : "tickets"}?`}
        danger
        icon={<CircleCheck className="h-[18px] w-[18px]" />}
        body={
          <>
            This closes{" "}
            <span className="font-semibold">
              {bulk?.rows.length ?? 0} {(bulk?.rows.length ?? 0) === 1 ? "ticket" : "tickets"}
            </span>{" "}
            and removes them from the open queue. Guests can reopen by replying.
          </>
        }
        confirmLabel={bulkClose.isPending ? "Closing…" : "Close all"}
        busy={bulkClose.isPending}
        onConfirm={() => bulk?.kind === "close" && bulkClose.mutate(bulk.rows)}
      />
    </RoutedCollectionLayout>
  )
}

// Team display options for the assignee picker.
function memberOptions(members: MemberRow[]): Array<{ userId: string; name: string }> {
  const out: Array<{ userId: string; name: string }> = []
  for (const m of members) {
    const id = m.user?.id ?? m.userId
    const name = m.user?.name ?? m.user?.email ?? null
    if (id && name) out.push({ userId: id, name })
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}

function cap(s: string): string {
  return s.replace(/^./, (c) => c.toUpperCase())
}
function relTime(iso: string): string {
  const t = Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`)
  if (Number.isNaN(t)) return ""
  const m = Math.round((Date.now() - t) / 60000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}
